import { get } from "lodash";
import { parse } from "url";

export const getUnifiedIdFromPath = (pathname = "") => {
  let split1 = pathname.split("/c/");
  split1 = split1.length > 1 ? split1[1] : "";
  let split2 = split1.split("_/");
  split2 = split2.length ? split2[0] : "";
  // remove trailing slash in case '/_' was not present
  split2 = split2.replace(/\/$/, "");
  let categoryID = split2.split("/");
  categoryID = categoryID.length ? categoryID[0] : "";
  return categoryID;
};

export const getNStateFromPath = (pathname = "") => {
  let split1 = pathname.split("_/");
  split1 = split1.length > 1 ? split1[1] : "";
  let split2 = split1.split("?");
  split2 = split2.length ? split2[0] : "";
  // remove trailing slash
  split2 = split2.replace(/\/$/, "");
  return split2;
};

const getFilteredDimensionName = (data, menuType) =>
  data
    .filter(({ type }) => (menuType ? type === menuType : Boolean))
    .reduce((res, { "dimension-name": dimensionName }) => dimensionName, "");

// active filters don't have 'type' data so using dimension name to find
const getActiveFilters = (data, filterByDimensionName) => {
  let refinementCrumbsArray = data.filter((item) =>
    item.hasOwnProperty("refinement-crumbs")
  );
  refinementCrumbsArray = refinementCrumbsArray.length
    ? refinementCrumbsArray[0]["refinement-crumbs"]
    : [];
  return refinementCrumbsArray
    .filter(({ "dimension-name": dimensionName }) =>
      filterByDimensionName ? dimensionName === filterByDimensionName : Boolean
    )
    .map(
      ({ count, label, "remove-action": removeAction }, index) =>
        filterByDimensionName && {
          active: true,
          url: `${get(removeAction, "content-path")}${get(
            removeAction,
            "navigation-state"
          )}`,
          heading: label,
          count,
        }
    )
    .filter(Boolean);
};

const getNonActiveFilters = (data, menuType) =>
  data
    .filter(({ type }) => (menuType ? type === menuType : Boolean))
    .reduce((res, { refinements }) => [...res, ...refinements], [])
    .map(
      (
        {
          count,
          label,
          "content-path": contentPath,
          "navigation-state": navState,
          properties,
        },
        index
      ) =>
        menuType && {
          active: false,
          url: `${contentPath}${navState}`,
          heading: label,
          count,
        }
    )
    .filter(Boolean);

const getFilterType = (data, menuType) =>
  data
    .filter(({ type }) => (menuType ? type === menuType : Boolean))
    .reduce((res, { name }) => name, "");

const getSortOrder = (data, menuType) =>
  data
    .filter(({ type }) => (menuType ? type === menuType : Boolean))
    .reduce(
      (res, { "refinement-order": refinementOrder }) =>
        refinementOrder ? refinementOrder : [],
      []
    );

const sortFilters = (filterArray, orderArray, orderKey) => {
  if (!orderArray.includes(undefined)) {
    filterArray.sort((a, b) => {
      const A = a[orderKey];
      const B = b[orderKey];

      return orderArray.indexOf(A) > orderArray.indexOf(B) ? 1 : -1;
    });
  }

  return filterArray;
};

// Data inside 'secondary-content' is not always returned in the same order, so we cannot rely on index to find data
export const getNavigationArray = (data) => {
  // find `ContentSlotSecondary`array (which contains 'contents')
  const contentsArray = data.filter(
    (item) => item.type === "ContentSlotSecondary"
  );

  // if contentsArray exists, find which array within 'contents' contains 'navigation'
  const navigationArray = contentsArray.length
    ? contentsArray[0]["contents"].filter((item) =>
        item.hasOwnProperty("navigation")
      )
    : [];
  return get(navigationArray, '[0]["navigation"]', []);
};

/**
 * Checks if a string matches the legacy CDP URL pattern using N-values
 *
 * @param {string} path - The path to check.
 * @returns {boolean} True if the string includes "/_/"; false otherwise.
 */

export const checkIfLegacyNValuePath = (string = "") => {
  return string?.includes("/_/");
};

/**
 * Extracts the CDP hash from a URL path.
 *
 * This function splits the URL by the "?" character to remove any URL parameters,
 * then uses a regex to get the last path segment regardless of the number of trailing slashes.
 * It returns the last segment of the URL if it does not start with "N-", otherwise it returns null.
 *
 * @param {string} url - The URL to extract the CDP hash from.
 * @returns {string|null} The CDP hash if it exists, null otherwise.
 */

export const getCdpHashFromPath = (url) => {
  // Return null if url is falsey
  if (!url) return null;
  const lastSegment = url.split("?")[0].match(/([^\/]+)\/*$/)[1] || "";
  return lastSegment.startsWith("N-") ? null : lastSegment;
};

/**
 * Parses the given path and returns an array of styleboost values that match a specific pattern.
 *
 * @param {string} path - The path to parse for styleboost values.
 * @returns {string[]} An array of styleboost values that match the pattern /^[A-Z0-9]+-[0-9]+$/, or empty [] if no such values are found.
 *
 * @example
 * // returns ["ABC-123", "DEF-456"]
 * getStyleboostFromPath("/test?styleboost=ABC-123,DEF-456,GHI")
 */

export const getStyleboostArrayFromPath = (path = "") => {
  if (!path) return [];

  const urlWithParsedQuery = parse(path, true);
  const regex = /^[A-Z0-9]+-[0-9]+$/;
  return (
    urlWithParsedQuery?.query?.styleboost
      ?.split(",")
      ?.filter((item) => regex.test(item)) || []
  );
};
